import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X } from "lucide-react";
import Wordmark from "./Wordmark";

const links = [
  { to: "/golfers", label: "Golfers" },
  { to: "/sponsors", label: "Sponsorship" },
  { to: "/about", label: "About" },
  { to: "/apply", label: "Apply" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-40" data-testid="top-navbar">
      {/* Brand strip — collapses on scroll */}
      <div
        className={`bg-foreground text-background overflow-hidden transition-all duration-300 ease-out ${
          scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"
        }`}
        data-testid="brand-strip"
      >
        <div className="container-tight h-9 flex items-center justify-between text-[10px] uppercase tracking-[0.22em]">
          <span className="opacity-70">Backed by Talk Golf International · Est. 2026</span>
          <span className="hidden md:inline opacity-70">Supporting the next generation of golf talent</span>
        </div>
      </div>

      {/* Main nav — shrinks ~10% on scroll */}
      <div className="backdrop-blur-xl bg-background/85 border-b border-border">
        <div
          className={`container-tight flex items-center justify-between transition-all duration-300 ease-out ${
            scrolled ? "h-[58px]" : "h-[72px]"
          }`}
        >
          <div
            className={`transition-transform duration-300 ease-out origin-left ${
              scrolled ? "scale-[0.9]" : "scale-100"
            }`}
          >
            <Wordmark size="md" />
          </div>

          <nav className="hidden md:flex items-center gap-9" data-testid="nav-links">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.18em] font-medium transition-colors ${
                    isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <button
                  className="text-[13px] uppercase tracking-[0.18em] text-foreground/70 hover:text-foreground"
                  onClick={() => navigate("/dashboard")}
                  data-testid="nav-dashboard-btn"
                >
                  {user.name?.split(" ")[0] || "Account"}
                </button>
                <button className="btn-outline text-[12px] tracking-[0.18em] uppercase" onClick={logout} data-testid="nav-logout-btn">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-[13px] uppercase tracking-[0.18em] text-foreground/70 hover:text-foreground" data-testid="nav-login-link">
                  Sign in
                </Link>
                <Link to="/golfers" className="btn-primary text-[12px] tracking-[0.18em] uppercase" data-testid="nav-support-btn">
                  Back a golfer
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-b border-border bg-background" data-testid="nav-mobile-panel">
          <div className="container-tight py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-base uppercase tracking-[0.18em]"
                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="text-left text-base uppercase tracking-[0.18em]" data-testid="nav-mobile-dashboard-btn">
                  Dashboard
                </button>
                <button onClick={() => { setOpen(false); logout(); }} className="btn-outline" data-testid="nav-mobile-logout-btn">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-base uppercase tracking-[0.18em]" data-testid="nav-mobile-login-link">Sign in</Link>
                <Link to="/golfers" onClick={() => setOpen(false)} className="btn-primary text-center" data-testid="nav-mobile-support-btn">
                  Back a golfer
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
